import React, { useState } from 'react';
import {
  BookOpen, Building2, Upload, Users, Shield, GitBranch, Search,
  ChevronRight, Check, ArrowRight, Database, Settings, Bell,
  FileText, MessageSquare, BarChart3, Lightbulb, AlertTriangle,
  Briefcase, Zap, CheckSquare, ClipboardList, MousePointer,
  Download, History, Bot, StickyNote
} from 'lucide-react';
import './GuidePage.css';

const TABS = [
  { id: 'overview',   label: 'Quick Start',         icon: Zap },
  { id: 'pipeline',   label: 'Pipeline & CRM',       icon: GitBranch },
  { id: 'deals',      label: 'Deal Workspace',        icon: Building2 },
  { id: 'diligence',  label: 'Due Diligence & AI',    icon: Shield },
  { id: 'findings',   label: 'Findings Notes',        icon: StickyNote },
  { id: 'documents',  label: 'Documents & Chat',      icon: FileText },
  { id: 'team',       label: 'Team & Permissions',    icon: Users },
  { id: 'settings',   label: 'Settings',              icon: Settings },
];

function Section({ title, children }) {
  return (
    <div className="guide-section">
      <h2 className="guide-section-title">{title}</h2>
      {children}
    </div>
  );
}

function Step({ number, title, children }) {
  return (
    <div className="guide-step">
      <div className="guide-step-number">{number}</div>
      <div className="guide-step-body">
        <h4 className="guide-step-title">{title}</h4>
        <div className="guide-step-content">{children}</div>
      </div>
    </div>
  );
}

function Tip({ children }) {
  return (
    <div className="guide-tip">
      <Lightbulb size={15} className="guide-tip-icon" />
      <span>{children}</span>
    </div>
  );
}

function Warning({ children }) {
  return (
    <div className="guide-warning">
      <AlertTriangle size={15} className="guide-warning-icon" />
      <span>{children}</span>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, description }) {
  return (
    <div className="guide-feature-card">
      <div className="guide-feature-icon"><Icon size={18} /></div>
      <div>
        <h4>{title}</h4>
        <p>{description}</p>
      </div>
    </div>
  );
}

// ── Tab Content ──────────────────────────────────────────────────────────────

function OverviewTab() {
  return (
    <div>
      <div className="guide-hero-banner">
        <div className="guide-hero-badge"><Zap size={14} /> Welcome to DealNexa</div>
        <h1>Your Complete M&amp;A Platform Guide</h1>
        <p>
          DealNexa is a deal management platform built for M&amp;A teams — private equity,
          corporate development, and advisors. Track deals, run AI-powered due diligence,
          manage documents, and collaborate — all in one place.
        </p>
      </div>

      <Section title="What's in DealNexa">
        <div className="guide-feature-grid">
          <FeatureCard icon={GitBranch}    title="Pipeline"           description="Kanban board to track every deal by stage — from first look to close." />
          <FeatureCard icon={Database}     title="CRM"                description="Import deals from CSV/Excel/Salesforce/HubSpot. Full deal history." />
          <FeatureCard icon={Building2}    title="Deal Workspace"     description="Each deal has its own room: documents, tasks, notes, contacts, and AI analysis." />
          <FeatureCard icon={Shield}       title="AI Due Diligence"   description="Upload docs and the AI generates a formal DD report with CRITICAL/HIGH/MEDIUM flags." />
          <FeatureCard icon={StickyNote}   title="Findings Notes"     description="Record manual observations by category. Download as a formatted report." />
          <FeatureCard icon={ClipboardList} title="Diligence Tracker" description="Checklist-based tracker to see what's complete, partial, or still open." />
          <FeatureCard icon={FileText}     title="Documents"          description="Upload PDFs and CSVs. AI analyzes each doc automatically." />
          <FeatureCard icon={MessageSquare} title="AI Chat"           description="Ask plain-English questions about your uploaded documents." />
          <FeatureCard icon={CheckSquare}  title="Tasks"              description="Deal-level to-dos with priority, assignee, and due date." />
          <FeatureCard icon={Users}        title="Team"               description="Role-based access control. Invite colleagues and advisors securely." />
        </div>
      </Section>

      <Section title="Getting started in 5 steps">
        <Step number={1} title="Create your organization">
          When you first sign up you'll be walked through a quick onboarding flow.
          Create an <strong>organization</strong> — your team's shared workspace. All deals,
          documents, and contacts are scoped to your org.
        </Step>
        <Step number={2} title="Invite your team">
          From the top-right user menu, click <strong>Members → Invite</strong>. Enter an
          email, choose a role (Admin, Member, Viewer, or Custom), and generate an invite link
          to share.
        </Step>
        <Step number={3} title="Create or import your first deal">
          Click <strong>Deals</strong> in the sidebar and hit <strong>+ New Deal</strong>, or
          use the <strong>CRM</strong> module to import existing deals from a CSV/Excel file.
        </Step>
        <Step number={4} title="Upload documents">
          Open a deal and go to the <strong>Documents</strong> tab, or click
          <strong> Upload Documents</strong> in the sidebar. After upload, the AI automatically
          analyzes each document.
        </Step>
        <Step number={5} title="Run AI due diligence">
          Inside a deal, click <strong>Due Diligence</strong> in the sidebar. Hit
          <strong> Run Analysis</strong> to generate a formal DD report. The analysis
          runs in the background — you can navigate away while it processes.
        </Step>
        <Tip>You can always return to this guide by clicking <strong>Getting Started</strong> at the bottom of the sidebar.</Tip>
      </Section>

      <Section title="Sidebar navigation">
        <p className="guide-para">The left sidebar is your main nav. Here's what each item does:</p>
        <div className="guide-nav-table">
          {[
            ['Home',                'Your dashboard — recent activity, deal stats, and quick-access widgets.'],
            ['Pipeline',            'Kanban board showing active deals by stage. Drag cards to move stages.'],
            ['CRM',                 'Full deal history including archived and imported deals.'],
            ['Active Diligence',    'Jump directly into the due diligence workspace for the selected deal.'],
            ['Due Diligence',       'AI-generated DD report and clause detection for the selected deal.'],
            ['Findings Notes',      'Record and manage your manual due diligence observations by category.'],
            ['Document Library',    'All uploaded documents across deals. Filter, search, and open any doc.'],
            ['Summaries',           'AI-generated summaries for each uploaded document.'],
            ['Tasks',               'Deal-level tasks with priority, assignee, and due date tracking.'],
            ['Opportunities Tracker','Structured checklist tracking the status of each diligence workstream.'],
            ['Contacts',            'People associated with your deals — sellers, advisors, lawyers.'],
            ['Upload Documents',    'Upload PDFs and CSVs for AI processing.'],
            ['AI Chat',             'Ask plain-English questions about your uploaded documents.'],
          ].map(([name, desc]) => (
            <div key={name} className="guide-nav-row">
              <span className="guide-nav-name">{name}</span>
              <span className="guide-nav-desc">{desc}</span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function PipelineTab() {
  return (
    <div>
      <Section title="Pipeline — Track deals stage by stage">
        <p className="guide-para">
          The Pipeline is a Kanban board showing all active deals organized by where they
          are in your M&amp;A process. Your whole team sees the same board in real time.
        </p>

        <Step number={1} title="Default stages">
          Deals move left to right through the following default stages:
          <ul className="guide-list">
            <li><strong>Level 1 Assessment</strong> — Initial screening and fit evaluation</li>
            <li><strong>Level 2 Assessment</strong> — Deeper review; management meetings</li>
            <li><strong>LOI</strong> — Letter of Intent signed; exclusivity begins</li>
            <li><strong>Final Agreements</strong> — Definitive documents being negotiated</li>
            <li><strong>Closed</strong> — Transaction completed</li>
            <li><strong>Post Close Integration</strong> — 100-day plan execution</li>
          </ul>
          <Tip>Rename, reorder, or add custom stages from the gear icon at the top right of the Pipeline page.</Tip>
        </Step>

        <Step number={2} title="Moving a deal">
          <strong>Kanban view:</strong> Drag a deal card from one column to another.<br /><br />
          <strong>List view:</strong> Click the stage dropdown on the deal row.<br /><br />
          Every stage change is saved instantly and logged in the audit trail.
        </Step>

        <Step number={3} title="Filtering the pipeline">
          Use the search bar and filter dropdowns at the top to narrow by owner, industry,
          or keywords. Useful when managing a large pipeline.
        </Step>

        <Step number={4} title="Archived and Discontinued deals">
          Deals that fall out of the active pipeline can be moved to <strong>Archived</strong>
          (on hold) or <strong>Discontinued</strong> (dead). They don't clutter your board
          but are never lost.
        </Step>
        <Warning>Deleting a deal is permanent. Use Archive or Discontinued for deals you may want to revisit later.</Warning>
      </Section>

      <Section title="CRM — Your complete deal history">
        <p className="guide-para">
          While the Pipeline shows active deals in progress, the CRM is the archive and
          source of truth for every deal your organization has ever touched — including
          prospects screened, deals lost, and data migrated from other tools.
        </p>

        <Step number={1} title="Importing from CSV, Excel, Salesforce, or HubSpot">
          <ol className="guide-ordered-list">
            <li>Open <strong>CRM</strong> from the sidebar.</li>
            <li>Click <strong>Import Data</strong> at the top right.</li>
            <li>Drag and drop your file (CSV or XLSX).</li>
            <li>DealNexa auto-maps known columns (Company Name, Deal Value, Stage, Owner, etc.).</li>
            <li>Unrecognized columns become custom fields — preserved exactly as-is.</li>
            <li>Preview the mapping, then click <strong>Import Records</strong>.</li>
          </ol>
          <Tip>Use the column mapping dropdowns during import to tell DealNexa which column is which. Everything unmapped becomes a custom column.</Tip>
        </Step>

        <Step number={2} title="Promoting a CRM deal to the Pipeline">
          <ol className="guide-ordered-list">
            <li>Check the box next to one or more CRM deals.</li>
            <li>Click <strong>Add to Pipeline</strong> in the action bar.</li>
            <li>Choose the starting stage and confirm.</li>
          </ol>
          The deal now appears on both the CRM and the Pipeline board. Stage changes in
          the Pipeline sync back to the CRM record.
        </Step>

        <Step number={3} title="Creating a deal in both CRM and Pipeline at once">
          When creating a new deal from <strong>Deals</strong> or <strong>Pipeline</strong>,
          check <strong>"Also add to CRM"</strong> to save it in both places simultaneously.
        </Step>
      </Section>
    </div>
  );
}

function DealsTab() {
  return (
    <div>
      <Section title="Deal Workspace — Everything for one deal">
        <p className="guide-para">
          Every deal has its own workspace. Open any deal to access documents, AI analysis,
          tasks, notes, and contacts — all in one place.
        </p>

        <Step number={1} title="Creating a new deal">
          <ol className="guide-ordered-list">
            <li>Click <strong>Deals</strong> in the sidebar.</li>
            <li>Click <strong>+ New Deal</strong> at the top right.</li>
            <li>Enter the deal name, industry, estimated value, target close date, and stage.</li>
            <li>Optionally assign an owner and a code name.</li>
            <li>Click <strong>Create Deal</strong>.</li>
          </ol>
          <Tip>The deal value is used to calculate total pipeline value shown on the home dashboard.</Tip>
        </Step>

        <Step number={2} title="Deal tabs">
          Inside a deal you'll find these tabs:
          <ul className="guide-list">
            <li><strong>Overview</strong> — Key stats, stage, value, close date, description, and team members.</li>
            <li><strong>Documents</strong> — Upload and manage all documents for this deal.</li>
            <li><strong>Due Diligence</strong> — AI-generated DD report and clause detection.</li>
            <li><strong>Tasks</strong> — Create and track to-do items for the deal.</li>
            <li><strong>Notes</strong> — Free-form team notes, timestamped and attributed.</li>
            <li><strong>Contacts</strong> — People associated with this deal.</li>
          </ul>
        </Step>

        <Step number={3} title="Tasks">
          Go to the <strong>Tasks</strong> tab inside a deal to manage deal-level work:
          <ul className="guide-list">
            <li>Click <strong>Add Task</strong> to create a new task.</li>
            <li>Set a <strong>title</strong>, <strong>priority</strong> (High / Medium / Low),
              <strong> assignee</strong>, and <strong>due date</strong>.</li>
            <li>Check the checkbox next to a task to mark it complete. Check again to reopen.</li>
            <li>Overdue tasks are highlighted in red.</li>
          </ul>
          <Tip>Assign tasks to specific team members so everyone knows who owns what.</Tip>
        </Step>

        <Step number={4} title="Notes">
          The <strong>Notes</strong> tab is for free-form team communication about a deal.
          Each note is saved with your name and a timestamp. Notes are visible to all team
          members with access to the deal.
        </Step>

        <Step number={5} title="Contacts">
          The <strong>Contacts</strong> tab tracks all people associated with the deal:
          sellers, advisors, lawyers, lenders, and other parties. For each contact you can
          record their name, role, email, phone, and which side of the deal they're on
          (buy-side, sell-side, advisor, lender, etc.).
        </Step>

        <Step number={6} title="Moving a deal between stages">
          Change the stage from inside the deal using the <strong>Stage</strong> dropdown,
          or drag the card on the Pipeline board. All changes are logged automatically.
        </Step>
      </Section>
    </div>
  );
}

function DiligenceTab() {
  return (
    <div>
      <Section title="AI Due Diligence — Formal DD report in minutes">
        <p className="guide-para">
          DealNexa's AI reads every document you upload and produces a formal due diligence
          assessment — complete with a flag register, executive summary, section-by-section
          analysis, and required next steps. Select a deal, upload its documents, then run
          the analysis.
        </p>

        <Step number={1} title="Running an analysis">
          <ol className="guide-ordered-list">
            <li>Open a deal and click <strong>Due Diligence</strong> in the sidebar
              (under the deal name), or navigate to <strong>Active Diligence</strong>.</li>
            <li>Make sure documents have been uploaded to the deal.</li>
            <li>Click <strong>Run Analysis</strong>.</li>
            <li>An <strong>intake form</strong> will appear — fill in the deal stage,
              deal type, and reviewer name to help the AI tailor its output.</li>
            <li>Click <strong>Start Analysis</strong>. The analysis begins immediately.</li>
          </ol>
          <Tip>You can navigate away while the analysis runs. A progress indicator appears at the bottom-right of every page — you'll see the stage update in real time.</Tip>
        </Step>

        <Step number={2} title="The DD Report tab">
          Once the analysis is complete, the <strong>DD Report</strong> tab shows a formal
          due diligence assessment including:
          <ul className="guide-list">
            <li><strong>Executive Summary</strong> — High-level deal narrative and financial snapshot.</li>
            <li><strong>Consolidated Flag Register</strong> — Every flag in the report with ID,
              severity, area, and summary. Click any row to expand the full detail.</li>
            <li><strong>Detailed Sections</strong> — Financial, Operational, Human Capital,
              Legal &amp; Contractual, and Governance DD with full analysis per flag.</li>
            <li><strong>Required Next Steps</strong> — Prioritized action items before LOI.</li>
            <li><strong>Documents Reviewed</strong> — Full list of source documents.</li>
          </ul>
          Flag severities:
          <ul className="guide-list">
            <li><strong style={{color:'#dc2626'}}>CRITICAL</strong> — Deal-stopper; must be resolved before LOI.</li>
            <li><strong style={{color:'#d97706'}}>HIGH</strong> — Material item requiring management response and validation.</li>
            <li><strong style={{color:'#2563eb'}}>MEDIUM</strong> — Requires monitoring, modeling, or contractual protection.</li>
          </ul>
        </Step>

        <Step number={3} title="Adding report text to Finding Notes">
          Any text in the DD report can be saved directly to your Finding Notes:
          <ol className="guide-ordered-list">
            <li>Select any text in the report by clicking and dragging.</li>
            <li>A dark <strong>"+ Add to Finding Notes"</strong> button appears above the selection.</li>
            <li>Click it — a modal opens pre-filled with your selected text as the finding.</li>
            <li>Choose a category, add a key question and source document if needed, then click
              <strong> Add Finding</strong>.</li>
          </ol>
          <Tip>Use this to pull specific flags or passages from the AI report directly into your structured finding notes without retyping.</Tip>
        </Step>

        <Step number={4} title="Downloading the report as PDF">
          Click the <strong>Download PDF</strong> button at the top of the DD Report tab.
          The report is exported as a formatted A4 PDF with your deal name, date, and status
          in the header — ready to share with your IC or board.
        </Step>

        <Step number={5} title="Clause Detection tab">
          Switch to the <strong>Clause Detection</strong> tab to see specific contract clauses
          the AI identified in your documents:
          <ul className="guide-list">
            <li>Each clause shows the type, risk level, confidence score, and a text excerpt.</li>
            <li>Click <strong>+ Add to Finding Notes</strong> on any clause to save it as a
              structured finding note automatically.</li>
          </ul>
        </Step>

        <Step number={6} title="Analysis history">
          Click the <strong>history icon</strong> (clock icon) next to the Re-analyze button
          to see all previous analysis runs for this deal. Click any past run to load it and
          compare it with the current one.
        </Step>

        <Warning>The AI reads only the documents you have uploaded. Missing documents mean missing findings. Always verify critical figures against the source document.</Warning>
      </Section>

      <Section title="Opportunities Tracker — Diligence workstream checklist">
        <p className="guide-para">
          The <strong>Opportunities Tracker</strong> (in the sidebar under a deal) gives you
          a structured checklist view of every diligence workstream — financial, legal,
          commercial, and more.
        </p>
        <Step number={1} title="Using the tracker">
          <ul className="guide-list">
            <li>Each workstream has line items you can mark as <strong>Complete</strong>,
              <strong> Partial</strong>, or <strong>Open</strong>.</li>
            <li>Add comments and notes to individual line items.</li>
            <li>The tracker shows an overall completion percentage across all workstreams.</li>
            <li>Use this as your go-to status update for IC meetings and management reviews.</li>
          </ul>
        </Step>
      </Section>
    </div>
  );
}

function FindingsTab() {
  return (
    <div>
      <Section title="Findings Notes — Your structured analysis log">
        <p className="guide-para">
          Findings Notes is where you record your own manual observations and analysis —
          organized by category, timestamped, and downloadable as a formatted report.
          Think of it as the analyst's notebook that lives alongside the AI report.
        </p>

        <Step number={1} title="Adding a finding">
          <ol className="guide-ordered-list">
            <li>Click <strong>Findings Notes</strong> in the sidebar under a deal.</li>
            <li>Click <strong>+ Add Finding</strong>.</li>
            <li>Fill in:
              <ul className="guide-list" style={{marginTop:6}}>
                <li><strong>Date</strong> — When you recorded this finding.</li>
                <li><strong>Category</strong> — The diligence area (see below).</li>
                <li><strong>Key Question</strong> — (Optional) The question this finding answers.</li>
                <li><strong>Finding</strong> — Your observation, risk, or opportunity.</li>
                <li><strong>Source Document</strong> — (Optional) Which document this came from.</li>
              </ul>
            </li>
            <li>Click <strong>Add Finding</strong> to save.</li>
          </ol>
        </Step>

        <Step number={2} title="Finding categories">
          Findings are organized into the following categories:
          <ul className="guide-list">
            <li>Clients &amp; Revenue</li>
            <li>Front Office (Sales &amp; Delivery)</li>
            <li>Operations &amp; Back Office</li>
            <li>Legal &amp; Corporate</li>
            <li>Finance, Accounting, Tax, QoE, NWC</li>
            <li>Valuation/Terms Items</li>
            <li>Integration Items</li>
            <li>Miscellaneous</li>
          </ul>
          <Tip>These categories match the standard M&amp;A due diligence workstreams — your findings will map directly to the sections of a typical IC memo.</Tip>
        </Step>

        <Step number={3} title="Adding findings from the DD Report">
          You don't have to type everything manually. In the <strong>DD Report</strong> tab,
          select any text and click the <strong>"+ Add to Finding Notes"</strong> tooltip that
          appears. The selected text will be pre-filled into the finding form.
        </Step>

        <Step number={4} title="Adding findings from Clause Detection">
          In the <strong>Clause Detection</strong> tab, click <strong>+ Add to Finding Notes</strong>
          on any detected clause. The clause detail, risk level, and excerpt are saved as a
          structured finding automatically.
        </Step>

        <Step number={5} title="Editing and deleting findings">
          Click the <strong>pencil icon</strong> on any finding to edit it. Click the
          <strong> trash icon</strong> to delete. Deletions are permanent.
        </Step>

        <Step number={6} title="Downloading the findings report">
          Click <strong>Download Report</strong> at the top of the Findings Notes page to
          export all findings as a formatted HTML report organized by category — ready to
          print or share with your team.
        </Step>
      </Section>
    </div>
  );
}

function DocumentsTab() {
  return (
    <div>
      <Section title="Documents — Upload, analyze, and search">
        <p className="guide-para">
          Documents are at the center of every deal. DealNexa accepts PDFs and CSVs and
          runs AI analysis automatically after upload.
        </p>

        <Step number={1} title="Uploading documents">
          <ol className="guide-ordered-list">
            <li>Click <strong>Upload Documents</strong> in the sidebar, or open a deal and
              go to the <strong>Documents</strong> tab.</li>
            <li>Select the deal you want to attach the documents to.</li>
            <li>Drag and drop your files or click to browse. Multiple files at once are supported.</li>
            <li>DealNexa processes each document — parsing text, extracting structure, and
              running AI analysis. Processing time depends on document size.</li>
            <li>Once complete, the document appears in the <strong>Document Library</strong>.</li>
          </ol>
          <Tip>Upload all documents for a deal before running the AI analysis. The more documents available, the more comprehensive the DD report.</Tip>
        </Step>

        <Step number={2} title="Document Library">
          The <strong>Document Library</strong> shows all documents across all deals.
          Filter by deal, type, or status. Click any document to view its details,
          analysis, and summary.
        </Step>

        <Step number={3} title="Summaries">
          Navigate to <strong>Summaries</strong> in the sidebar to see AI-generated summaries
          for each uploaded document. Each summary includes:
          <ul className="guide-list">
            <li>A concise overview of what the document contains</li>
            <li>Key financial figures or terms extracted</li>
            <li>Notable points flagged by the AI</li>
          </ul>
        </Step>

        <Step number={4} title="AI Chat">
          Click <strong>AI Chat</strong> in the sidebar to ask questions about your uploaded
          documents in plain English. The AI searches across all documents linked to the
          selected deal and returns a cited answer. Example questions:
          <ul className="guide-list">
            <li>"What is the revenue for fiscal year 2024?"</li>
            <li>"Are there any change of control provisions in the purchase agreement?"</li>
            <li>"What is the customer concentration?"</li>
            <li>"What are the key terms of the employment agreements?"</li>
            <li>"Summarize the IP ownership clauses."</li>
          </ul>
          <Tip>The AI Chat works best when all deal documents are uploaded first. The more context it has, the more accurate the answers.</Tip>
        </Step>

        <Warning>The AI reads the documents you upload. Scanned PDFs with poor image quality, handwritten pages, or missing pages may result in incomplete analysis. Use digital/text-based PDFs where possible.</Warning>
      </Section>
    </div>
  );
}

function TeamTab() {
  return (
    <div>
      <Section title="Team & Permissions — Collaborate securely">
        <p className="guide-para">
          DealNexa is built for teams. Invite colleagues and advisors with exactly the
          right level of access. Every action is logged in a permanent audit trail.
        </p>

        <Step number={1} title="Inviting team members">
          <ol className="guide-ordered-list">
            <li>Click your name or avatar at the <strong>top right</strong>.</li>
            <li>Select <strong>Members</strong>.</li>
            <li>Click the <strong>Invite</strong> tab.</li>
            <li>Enter the person's email and choose their role.</li>
            <li>Click <strong>Generate Link</strong> — this creates a shareable invite link.</li>
            <li>Copy the link and send it. When they click it, they'll create an account
              and join your organization.</li>
          </ol>
        </Step>

        <Step number={2} title="Roles">
          <div className="guide-roles-table">
            <div className="guide-role-row">
              <span className="guide-role-badge admin">Admin</span>
              <span>Full access — manage team, change permissions, see audit trail, edit all data.</span>
            </div>
            <div className="guide-role-row">
              <span className="guide-role-badge member">Member</span>
              <span>View and edit deals, documents, and contacts. Cannot manage team or see the audit trail.</span>
            </div>
            <div className="guide-role-row">
              <span className="guide-role-badge viewer">Viewer</span>
              <span>Read-only. Can see everything but cannot create, edit, or delete anything.</span>
            </div>
            <div className="guide-role-row">
              <span className="guide-role-badge custom">Custom</span>
              <span>Fully configurable. Admins set exactly which actions this role can perform.</span>
            </div>
          </div>
          <Tip>Use the <strong>Custom</strong> role for outside advisors — for example, a lawyer who needs to view documents but not edit deals.</Tip>
        </Step>

        <Step number={3} title="Changing a member's role">
          In <strong>Members</strong>, admins can change any member's role using the dropdown
          next to their name. Changes take effect immediately.
        </Step>

        <Step number={4} title="Per-member permissions">
          For fine-grained control, go to <strong>Members → Permissions → Per Member</strong>.
          Control exactly which resources (Pipeline, Deals, Documents, Findings Notes, etc.)
          a specific person can view, edit, delete, or administer.
        </Step>

        <Step number={5} title="Removing a member">
          In the Members tab, click the <strong>trash icon</strong> next to a member's name.
          They are removed immediately and lose all access.
        </Step>
      </Section>
    </div>
  );
}

function SettingsTab() {
  return (
    <div>
      <Section title="Settings — Customize your experience">
        <p className="guide-para">
          Access Settings from the top-right user menu → <strong>Settings</strong>.
        </p>

        <Step number={1} title="General">
          View your organization name, type, your role, and email address. See a summary
          of platform features available to your organization.
        </Step>

        <Step number={2} title="Home customization">
          The <strong>Home Customization</strong> tab controls which widgets appear on your
          home dashboard and in what order.
          <ul className="guide-list">
            <li>Toggle <strong>Visible / Hidden</strong> on any widget to show or hide it.</li>
            <li>Drag widgets by the grip handle on the left to reorder them.</li>
            <li>Click <strong>Save Layout</strong> when done.</li>
            <li>Click <strong>Reset to Default</strong> to restore the original layout.</li>
          </ul>
          <Tip>Your dashboard layout is personal — other team members have their own layout that your changes don't affect.</Tip>
        </Step>

        <Step number={3} title="Audit Trail (Admins only)">
          The <strong>Audit Trail</strong> is a complete, permanent log of every action in
          your organization:
          <ul className="guide-list">
            <li>Deals created, edited, and deleted</li>
            <li>Documents uploaded and analyzed</li>
            <li>Findings added and changed</li>
            <li>Team members invited and removed</li>
            <li>Permissions changed</li>
          </ul>
          Every event shows the date/time, the user who acted, what they did, and which
          resource was affected. Use filters to search by user, action type, or date range.
          <Warning>The Audit Trail cannot be edited or deleted. It is a permanent, tamper-proof record.</Warning>
        </Step>
      </Section>
    </div>
  );
}

const TAB_CONTENT = {
  overview:  <OverviewTab />,
  pipeline:  <PipelineTab />,
  deals:     <DealsTab />,
  diligence: <DiligenceTab />,
  findings:  <FindingsTab />,
  documents: <DocumentsTab />,
  team:      <TeamTab />,
  settings:  <SettingsTab />,
};

const GuidePage = () => {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="guide-page">
      <div className="guide-header">
        <div className="guide-header-inner">
          <BookOpen size={22} />
          <h1>DealNexa Guide</h1>
        </div>
        <p className="guide-header-sub">
          Everything you need to know to get the most out of DealNexa.
        </p>
      </div>

      <div className="guide-layout">
        <nav className="guide-nav">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`guide-nav-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={15} />
                {tab.label}
                {activeTab === tab.id && <ChevronRight size={14} className="guide-nav-chevron" />}
              </button>
            );
          })}
        </nav>

        <div className="guide-content">
          {TAB_CONTENT[activeTab]}
        </div>
      </div>
    </div>
  );
};

export default GuidePage;
